import { Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Currency } from '@domain/model/enums/currency/currency.enum';
import { TariffType } from '@domain/model/enums/currency/tariff-type.enum';
import { DocumentType } from '@domain/model/enums/document-type/document-type.enum';
import { EmailType } from '@domain/model/enums/email/email.enum';
import { ParametersService } from '@modules/business-accounts/services/parameters/parameters.service';
import { MAX_NUMBER_ACCOUNTS } from '@modules/business-accounts/utils/constants/user-workflow-constants';
import { IBaseFormBuilder } from '../../builders/base-builder.class';
import { forceValueValidator } from '../validators/force-value.validator';

@Injectable({
  providedIn: 'root',
})
export class ConfigNaturalPersonBuilder implements IBaseFormBuilder {
  private form!: FormGroup;
  currency: Currency | null = Currency.BOTH;

  paramsService!: ParametersService;

  constructor() {
    this.paramsService = ParametersService.instance;
    this.form = new FormGroup({});
  }

  public build(dataFormValue: any = {}): void {
    this.addConfigFields(dataFormValue);
    this.addCompanyFields(dataFormValue);
    this.addCustomerFields(dataFormValue);
    this.addStoreFields();
    this.addAgencyFields(dataFormValue);
    // this.addTaxDetailsFields(dataFormValue);
  }

  getResult(): FormGroup {
    return this.form;
  }

  // private addTaxDetailsFields(dataFormValue: any) {
  //   dataFormValue.foreignCountryTaxDetails = dataFormValue.foreignCountryTaxDetails || {};
  //   this.form.addControl(
  //     'taxDetailsData',
  //     new FormGroup({
  //       country: new FormControl(dataFormValue.foreignCountryTaxDetails.country),
  //       city: new FormControl(dataFormValue.foreignCountryTaxDetails.city),
  //       legalAddress: new FormControl(dataFormValue.foreignCountryTaxDetails.legalAddress),
  //       taxNumber: new FormControl(dataFormValue.foreignCountryTaxDetails.taxNumber),
  //       countryDoesNotRequireTaxNumber: new FormControl(
  //         dataFormValue.foreignCountryTaxDetails.countryDoesNotRequireTaxNumber
  //       ),
  //     })
  //   );
  // }

  private addConfigFields(dataFormValue: any) {
    const { accountsNumber } = dataFormValue;
    if (accountsNumber && accountsNumber == MAX_NUMBER_ACCOUNTS - 1) {
      this.currency = null;
    }

    this.form.addControl(
      'configData',
      new FormGroup({
        currency: new FormControl(this.currency, [Validators.required]),
        bieAccess: new FormControl(false, [Validators.required]),
        tAndC: new FormControl(false, [forceValueValidator(true)]),
        personalData: new FormControl(false),
        tariff: new FormControl(TariffType.DIGITAL),
        accountNumber: new FormControl(false, [forceValueValidator(true)]),
        department: new FormControl(),
        province: new FormControl(),
      })
    );
  }

  private addCompanyFields(dataFormValue: any) {
    this.form.addControl(
      'companyData',
      new FormGroup({
        companyId: new FormControl(dataFormValue.companyId),
        identityDocument: new FormGroup({
          type: new FormControl(DocumentType.RUC),
          number: new FormControl(dataFormValue.companyDocumentNumber),
        }),
        mainActivity: new FormControl('PEOPLE'),
        businessName: new FormControl(dataFormValue.personalData.businessName),
        sectorCode: new FormControl(dataFormValue.sectorData.sectorCode),
        subSectorCode: new FormControl(dataFormValue.sectorData.subSectorCode),
      })
    );
  }

  private addCustomerFields(dataFormValue: any) {
    this.form.addControl(
      'customerData',
      new FormGroup({
        id: new FormControl(dataFormValue.customerId),
        identityDocument: new FormGroup({
          type: new FormControl(DocumentType.DNI),
          number: new FormControl(dataFormValue.documentNumber),
        }),

        firstName: new FormControl(dataFormValue.personalData.firstName),
        middleName: new FormControl(dataFormValue.personalData.middleName),
        lastName: new FormControl(dataFormValue.personalData.lastName),
        motherLastName: new FormControl(dataFormValue.personalData.motherLastName),
        gender: new FormControl(dataFormValue.personalData.gender),
        birthDate: new FormControl(dataFormValue.personalData.birthDate),
        maritalStatus: new FormControl(dataFormValue.personalData.maritalStatus),

        birthCountry: new FormControl('PE'),
        residenceCountry: new FormControl('PE'),
        nationality: new FormControl('PE'),

        phone: new FormGroup({
          type: new FormControl('MOBILE'),
          number: new FormControl(dataFormValue.personalData.phoneNumber),
          carrier: new FormControl(dataFormValue.personalData.phoneCarrier),
        }),
        address: new FormGroup({
          country: new FormControl(dataFormValue.personalData.country),
          department: new FormControl(dataFormValue.personalData.departmentName),
          district: new FormControl(dataFormValue.personalData.districtName),
          province: new FormControl(dataFormValue.personalData.provinceName),
          streetType: new FormControl(dataFormValue.personalData.streetType),
          streetName: new FormControl(dataFormValue.personalData.streetName),
          streetNumber: new FormControl(dataFormValue.personalData.streetNumber),
          block: new FormControl(dataFormValue.personalData.block),
          lot: new FormControl(dataFormValue.personalData.lot),
          apartment: new FormControl(dataFormValue.personalData.apartment),
          neighborhood: new FormControl(dataFormValue.personalData.neighborhood),
          landmark: new FormControl(dataFormValue.personalData.landmark),
        }),
        email: new FormGroup({
          value: new FormControl(dataFormValue.personalData.email),
          type: new FormControl(EmailType.PERSONAL),
        }),
        ldpd: new FormControl('true'),
      })
    );
  }

  private addStoreFields() {
    this.form.addControl(
      'storeData',
      new FormGroup({
        storeCode: new FormControl('898'),
      })
    );
  }

  private addAgencyFields(dataFormValue: any) {
    this.form.addControl(
      'agencyData',
      new FormGroup({
        department: new FormControl(null, Validators.required),
        departmentCode: new FormControl(null),
        province: new FormControl(null,[Validators.required]),
      })
    );
  }
}

export const getConfigDataFormInstance = ({ from: form }: { from: FormGroup }) => form.get('configData') as FormGroup;

export const getAgencyDataFormInstance = ({ from: form }: { from: FormGroup }) => form.get('agencyData') as FormGroup;
